import CommonIriDisplay from '../application-core/CommonIriDisplay';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import PropTypes from 'prop-types';
import React from 'react';
import DatatypeDisplay from '../core/DatatypeDisplay';
import Errored from '../core/Errored';
import TitledPanel from '../core/TitledPanel';
import withGS from '../core/withGShoc';
import GS from '../grafsync/grafsync';
import { getIRIsForPropertyList, getValuesForPropertyList, getIRIsForProperty } from './utils';
import OntologyRelationshipsDisplay from '../vocabulary/taxonomies/OntologyRelationshipsDisplay';
import { BN_NAMESPACE } from '../core/utils/constants';

const CLASSES_TO_INCLUDE = [
    "http://www.w3.org/2004/02/skos/core#Concept",
    "http://www.w3.org/2008/05/skos-xl#Label",
    "http://www.w3.org/2004/02/skos/core#ConceptScheme",
    "http://www.w3.org/2004/02/skos/core#Collection",
    "http://www.w3.org/2004/02/skos/core#OrderedCollection"
];

class AssociatedPropertyDisplay extends React.Component {

    render() {

        const { data, defaultLang, resourceClasses, isReadOnly, components, projectGraph, associatedProjectGraph, linkedData, info,
            onAssociatedPropertyDelete, onAssociatedPropertyUpdate, viewLang, projectLang, searchClasses, applicableStatements,
            associatedProject, dialog, addNewResource, useWorkflow, reify, onReify, computeTemplates, suppress, sourceComponent,
            resourceData, projectIRI, id, includeExistingResource, addNewLiteral, addNewTypedLiteral, context, disjointProps,
            visiblePathList } = this.props;

        const resourceTypes = Array.isArray(data.type) ? data.type : [data.type];
        const isDeprecated = data.deprecated;
        const isDeleted = data.pendingDeletion;
        const isFunctional = resourceTypes.includes("FunctionalProperty");
        const isSymmetric = resourceTypes.includes("SymmetricProperty");
        const isBoolean = "range" in data && data.range.includes("http://www.w3.org/2001/XMLSchema#boolean");
        const property = id;
        const currentCardinality = resourceData[property] ?
            Array.isArray(resourceData[property]) ?
                resourceData[property].length
                :
                1
            :
            0;

        let disjointList = [];
        if (data.propertyDisjointWith) disjointList = [...data.propertyDisjointWith].filter(d => !d.startsWith(BN_NAMESPACE));
        const dps = disjointProps ?
            Object.keys(disjointProps).filter(p => disjointProps[p].includes(id))
            :
            [];
        disjointList = disjointList.concat(dps);

        const disjointValueList = (resourceTypes.includes("DatatypeProperty") || resourceTypes.includes("AnnotationProperty")) ?
            getValuesForPropertyList(resourceData, disjointList)
            :
            getIRIsForPropertyList(resourceData, disjointList);

        const valuesList = (resourceTypes.includes("DatatypeProperty") || resourceTypes.includes("AnnotationProperty")) ?
            (
                resourceData.hasOwnProperty(property) && resourceData[property] !== undefined ?
                    Array.isArray(resourceData[property]) ? resourceData[property] : [resourceData[property]]
                    :
                    []
            )
            :
            getIRIsForProperty(resourceData, property);

        const title = data.hasOwnProperty("label") ?
            data.label[defaultLang] ?
                data.label[defaultLang]
                :
                data.label["und"] ?
                    data.label["und"]
                    :
                    data.id
            :
            data.id;

        // We only display properties for which the domain is one of its classes (or superclasses)
        // We ignore blank nodes (i.e. restrictions)
        let isValidForDomain;
        try {
            const inferredResourceClasses = [].concat.apply([], resourceClasses
                .map(classIRI => CLASSES_TO_INCLUDE.includes(classIRI) ?
                    [classIRI]
                    :
                    GS.getSuperClasses(classIRI, associatedProjectGraph)));
            isValidForDomain = !data.domain || data.domain.length === 0
                || (data.domain.every(domainClass => domainClass.startsWith(BN_NAMESPACE)))
                || (data.domain.includes("http://www.w3.org/2002/07/owl#Thing") && data.domain.length === 1)
                || data.domain.some(domainClass => inferredResourceClasses.includes(domainClass));
            // If no existing values don't display
            if ((!isValidForDomain || (visiblePathList && !visiblePathList.includes(id))) && valuesList.length === 0) return null;
        }
        catch (e) {
            return <Errored data={e} />;
        }

        if (data._notAvailable) {
            return <Grid item xs={12} md={6}>
                <TitledPanel height="full" dataTest={"deleted-" + id} title={id}>
                    <Typography variant="body2">This has been deleted but has not been removed from associated project set up, or you do not have permission.</Typography>
                </TitledPanel>
            </Grid>;
        }

        let showReify = false;
        if (reify) {
            const templates = computeTemplates({
                forceBaseClass: true,
                baseInferredClass: "http://www.w3.org/1999/02/22-rdf-syntax-ns#Statement",
                resourceClasses: ["http://www.w3.org/1999/02/22-rdf-syntax-ns#Statement"],
                filterTemplatesByProperty: property
            }, ["http://www.w3.org/1999/02/22-rdf-syntax-ns#Statement"]);
            showReify = templates.length > 0;
        }

        const scList = data.range && !data.range.includes("http://www.w3.org/2002/07/owl#Thing") ?
            data.range.filter(r => !r.startsWith(BN_NAMESPACE))
            :
            [];

        let information = null;
        if (disjointList && disjointList.length > 0) {
            information = <span data-test="disjoint-info" style={{ display: 'block', marginBottom: 12, marginTop: 6 }}>
                <span style={{ display: 'block', maxWidth: '100%' }}>
                    <span style={{ display: 'block', transform: 'translateY(-2px)' }}>Disjoint with</span>
                    <span style={{ display: 'block', marginTop: 6, marginLeft: 12, maxWidth: '90%' }}>{
                        [...disjointList].sort().map(d => <CommonIriDisplay
                            id={d}
                            key={d}
                            compact={true}
                            wrap={false}
                            doNotdisplayNotLoaded={true}
                            components={components}
                            projectIRI={projectIRI}
                            handleNotAvailable={true}
                            graph={associatedProjectGraph}
                            viewLang={defaultLang}
                            defaultLang={defaultLang} />
                        )
                    }
                    </span>
                </span>
                <span style={{ display: 'block' }}>This property cannot share values with any of the disjoint properties.</span>
            </span>;
        }

        // If range includes owl:Thing then essentially it's everything so we just remove the range restriction
        return (
            <Grid item xs={12} lg={6} data-test={id}>
                {(resourceTypes.includes("DatatypeProperty") || resourceTypes.includes("AnnotationProperty")) &&
                    <DatatypeDisplay
                        title={title}
                        information={information}
                        info={info}
                        source={resourceData.id}
                        deleted={isDeleted}
                        deprecated={isDeprecated}
                        datatype={data.range && data.range.length > 0 ?
                            data.range[0]
                            :
                            this.props.overrideNullDatatype ?
                                ""
                                :
                                undefined}
                        defaultLang={defaultLang}
                        viewLang={viewLang}
                        projectLang={projectLang}
                        property={id}
                        useWorkflow={useWorkflow}
                        values={valuesList}
                        exclude={disjointValueList}
                        context={context}
                        reify={showReify}
                        onReify={onReify}
                        applicableStatements={applicableStatements}
                        searchClasses={searchClasses}
                        projectIRI={projectIRI}
                        onDelete={onAssociatedPropertyDelete}
                        graph={projectGraph}
                        sourceComponent={sourceComponent}
                        associatedProjectGraph={associatedProjectGraph}
                        onUpdate={onAssociatedPropertyUpdate}
                        isReadOnly={isReadOnly}
                        canAddValues={isValidForDomain}
                        addNewResource={addNewResource ? addNewResource(id, associatedProjectGraph, "@id") : null}
                        cardinalityAtMax={(isFunctional && currentCardinality > 0)
                            || (info.maxCount && currentCardinality >= info.maxCount)}
                        exceedsCardinality={((isFunctional || isBoolean) && currentCardinality > 1)
                            || (info.maxCount && currentCardinality > info.maxCount)}
                        onAddLiteral={addNewLiteral(data.id, associatedProjectGraph)}
                        onAddTypedLiteral={addNewTypedLiteral(data.id, associatedProjectGraph, data.domain ? [...data.domain] : null)} />
                }
                {resourceTypes.includes("ObjectProperty") &&
                    <OntologyRelationshipsDisplay
                        dialog={dialog}
                        useWorkflow={useWorkflow}
                        information={information}
                        title={title}
                        reify={showReify}
                        onReify={onReify}
                        applicableStatements={applicableStatements}
                        info={info}
                        deleted={isDeleted}
                        deprecated={isDeprecated}
                        resourceIRI={resourceData.id}
                        projectIRI={projectIRI}
                        source={id}
                        exclude={disjointValueList}
                        sourceComponent={sourceComponent}
                        suppressReason={suppress.includes("Symmetric") && isSymmetric ?
                            "This has been suppressed as it is a symmetric property."
                            :
                            suppress.includes("Inverse")
                                && (
                                    (data.inverseOf && ((Array.isArray(data.inverseOf) && data.inverseOf.length > 0) || !Array.isArray(data.inverseOf)))
                                    || info.hasInverse
                                ) ?
                                "This has been suppressed as it has inverse properties."
                                :
                                null
                        }
                        searchTypes={data.type.includes("AsymmetricProperty") ? ["Asymmetric"] : []}
                        graph={projectGraph}
                        range={data.range}
                        linkedData={linkedData}
                        associatedProject={associatedProject}
                        defaultLang={defaultLang}
                        viewLang={viewLang}
                        components={components}
                        associatedProjectGraph={associatedProjectGraph}
                        searchClasses={scList}
                        inferSubclasses={scList.length > 0}
                        property={id}
                        values={valuesList}
                        isReadOnly={isReadOnly}
                        canAddValues={isValidForDomain}
                        cardinalityAtMax={(isFunctional && currentCardinality > 0)
                            || (info.maxCount && currentCardinality >= info.maxCount)}
                        exceedsCardinality={(isFunctional && currentCardinality > 1)
                            || (info.maxCount && currentCardinality > info.maxCount)}
                        includeExistingResource={includeExistingResource ? includeExistingResource(property, associatedProjectGraph) : null} />
                }
            </Grid>
        );
    }
}

AssociatedPropertyDisplay.defaultProps = {
    info: {},
    searchClasses: [],
    suppress: [],
    overrideNullDatatype: false,
    useWorkflow: false
};

AssociatedPropertyDisplay.propTypes = {
    dialog: PropTypes.any,
    id: PropTypes.string.isRequired,
    info: PropTypes.object,
    projectIRI: PropTypes.string.isRequired,
    schemeIRI: PropTypes.string,
    sourceComponent: PropTypes.string,
    reify: PropTypes.bool,
    onReify: PropTypes.func,
    context: PropTypes.string,
    useWorkflow: PropTypes.bool,
    isReadOnly: PropTypes.bool.isRequired,
    components: PropTypes.array,
    viewLang: PropTypes.string,
    associatedProject: PropTypes.array,
    projectLang: PropTypes.array.isRequired,
    addNewLiteral: PropTypes.func.isRequired,
    addNewTypedLiteral: PropTypes.func.isRequired,
    linkedData: PropTypes.array,
    includeExistingResource: PropTypes.func,
    addNewResource: PropTypes.func,
    suppress: PropTypes.array,
    visiblePathList: PropTypes.array,
    projectType: PropTypes.string.isRequired,
    searchClasses: PropTypes.array,
    onAssociatedPropertyDelete: PropTypes.func.isRequired,
    onAssociatedPropertyUpdate: PropTypes.func.isRequired,
    graph: PropTypes.string.isRequired,
    resourceData: PropTypes.object.isRequired,
    defaultLang: PropTypes.string.isRequired,
    projectGraph: PropTypes.string.isRequired,
    disjointProps: PropTypes.object,
    associatedProjectGraph: PropTypes.string.isRequired,
    data: PropTypes.object.isRequired,
    computeTemplates: PropTypes.func,
    resourceClasses: PropTypes.array.isRequired
};

export default withGS(AssociatedPropertyDisplay);